import { Button } from "flowbite-react";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import useRazorpay, { RazorpayOptions } from "react-razorpay";
import { cancelPaymentById, completePaymentForDrive, createPaymentForDrive } from "src/apis/payment";
import { APP_ENDPOINT, RAZORPAY_APP_ID } from "src/config/env.config";
import { useToast } from "src/context/ToastProvider";
import { useUserContext } from "src/context/UserProvider";
import { JobseekerFormData } from "types";

const PlacementPreparationPage = ({ driveRegistrationApplication, driveApplicationId, driveRegistrationFormData }: { driveRegistrationApplication: any, driveApplicationId: string, driveRegistrationFormData: JobseekerFormData }) => {
    const toast = useToast();
    const { user: session } = useUserContext();
    const queryClient = useQueryClient();
    const Razorpay = useRazorpay();

    const [timeLeft, setTimeLeft] = useState<{
        days: number;
        hours: number;
        minutes: number;
        seconds: number;
    }>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    const applicationData = {
        time: "2025-02-07T18:30:00.000Z"
    };

    const positionData = {
        duration: 120
    };

    const pointersData = [
        {
            title: '6-Day tailored Job preparation with industry experts.',
            icon: 'https://masai-website-images.s3.ap-south-1.amazonaws.com/Clock_Clockwise_ezgif_com_png_to_webp_converter_878ca96fd4.webp',
        },
        {
            title: 'Exclusive 1-on-1 mentoring from top MAANG professionals.',
            icon: 'https://masai-website-images.s3.ap-south-1.amazonaws.com/Users_ezgif_com_png_to_webp_converter_2545fba93f.webp',
        },
        {
            title: 'Guaranteed 2 interviews or get your money back.',
            icon: 'https://masai-website-images.s3.ap-south-1.amazonaws.com/Hand_Coins_ezgif_com_png_to_webp_converter_0dce6a4f77.webp',
        },
    ];

    useEffect(() => {
        let timerInterval: ReturnType<typeof setInterval> | undefined;

        const initializeTimer = () => {
            const startDateTime = new Date(applicationData.time);

            if (Number.isNaN(startDateTime.getTime())) {
                console.error("Invalid Date constructed from:", applicationData.time);
                return;
            }

            // Calculate the session end time
            const sessionEndTime = new Date(
                startDateTime.getTime() + positionData.duration * 60 * 1000,
            );

            // Calculate the time difference
            const calculateTimeDifference = () =>
                Math.floor((startDateTime.getTime() - new Date().getTime()) / 1000);

            const formatTimeDifference = (timeDifference: number) => {
                const days = Math.floor(timeDifference / (24 * 3600));
                const hours = Math.floor((timeDifference % (24 * 3600)) / 3600);
                const minutes = Math.floor((timeDifference % 3600) / 60);
                const seconds = timeDifference % 60;

                return { days, hours, minutes, seconds };
            };

            const updateTimer = () => {
                const timeDifference = calculateTimeDifference();

                if (timeDifference > 0) {
                    const formattedTime = formatTimeDifference(timeDifference);
                    setTimeLeft(formattedTime);
                } else if (new Date() <= sessionEndTime) {
                    setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                } else {
                    setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                    clearInterval(timerInterval);
                }
            };

            updateTimer();
            timerInterval = setInterval(updateTimer, 1000);
        };

        initializeTimer();

        return () => {
            if (timerInterval) clearInterval(timerInterval);
        };
    }, []);

    const { days, hours, minutes, seconds } = timeLeft;

    const cancelPaymentFn = useMutation(['cancelPaymentById'], cancelPaymentById, {
        onSuccess() {
            toast({
                title: 'Payment Failed.',
                type: 'error',
            });
        },
        onError(error: Error) {
            const errorMessage = get(error, ['response', 'data', 'message'], error.message);
            toast({
                type: 'error',
                title: errorMessage,
            });
        },
    });

    const completePaymentForDriveMutation = useMutation(['completePaymentForDrive'], completePaymentForDrive, {
        onSuccess() {
            queryClient.invalidateQueries(['getDriveRegistrationApplication']);
            toast({
                title: 'Payment Done Successfully.',
                type: 'success',
            });
        },
        onError(error: Error) {
            const errorMessage = get(error, ['response', 'data', 'message'], error.message);
            toast({
                type: 'error',
                title: errorMessage,
            });
        },
    });

    const handleCompletePayment = (paymentId: string) => {
        completePaymentForDriveMutation.mutate({
            driveApplicationId: driveApplicationId || '',
            paymentId: paymentId,
        });

    };

    const createPaymentForDriveMutation = useMutation(['createPaymentForDrive'], createPaymentForDrive, {
        onSuccess(data, variables) {
            const finalPrice = data.finalAmount;
            const paymentId = data.payment?._id || '';
            console.log('session:', session);

            if (finalPrice != null && finalPrice > 0) {
                const options: RazorpayOptions = {
                    key: RAZORPAY_APP_ID,
                    amount: String(finalPrice),
                    currency: 'INR',
                    name: 'LevelUp',
                    description: `Transaction to buy a Service (Drive Registration)`,
                    image: `https://masai-website-images.s3.ap-south-1.amazonaws.com/svgviewer_png_output_8_a90c76ce97.png`,
                    callback_url: `${APP_ENDPOINT}/`,
                    order_id: data.payment?.orderId || '',
                    handler: () => {
                        handleCompletePayment(paymentId);
                    },
                    prefill: {
                        name: session?.name,
                        email: session?.email,
                        contact: session?.phone || '',
                    },
                    notes: {
                        address: 'LevelUp Corporate Office',
                    },
                    theme: {
                        color: '#7777E7',
                    },
                    modal: {
                        ondismiss: (() => {
                            cancelPaymentFn.mutate(paymentId);
                        }) as unknown as boolean,
                    },
                };
                const rzpay = new Razorpay(options);
                rzpay.on('payment.failed', function () {
                    cancelPaymentFn.mutate(paymentId);
                });
                rzpay.open();
            } else if (data) {
                if (paymentId) {
                    handleCompletePayment(paymentId);
                }
            } else {
                toast({
                    title: 'Payment creation failed',
                    type: 'error',
                });
            }
        },
        onError(error: Error) {
            const errorMessage = get(error, ['response', 'data', 'message'], error.message);
            toast({
                type: 'error',
                title: errorMessage,
            });
        },
    });

    const handleCreatePayment = () => {
        if (driveApplicationId) {
            createPaymentForDriveMutation.mutate({
                driveApplicationId: driveApplicationId || '',
            });
        } else {
            toast({
                type: 'error',
                title: 'Drive application id is required',
            });
        }
    };

    return (
        <div className="px-4 w-full h-fit">
            <div className="rounded-[8px] w-full max-w-full md:max-w-[720px] h-full">
                <div className="w-full p-[16px] md:p-0 md:w-[589px] mx-auto md:mt-[20px] flex flex-col justify-center">
                    <div className="flex flex-col justify-center items-start gap-1">
                        <p className="text-[#0A0103] text-[18px] leading-[24px] font-inter font-[700]">Reserve your spot in {driveRegistrationApplication?.location} Placement Drive</p>
                        <p className="text-[#0A0103] text-[16px] leading-[24px] font-inter font-[600]">Seats are limited—secure your place now with a ₹{driveRegistrationFormData?.jobType == 'Tech_Jobs' ? driveRegistrationApplication?.driveDetails?.meta?.secondPaymentAmount : driveRegistrationApplication?.driveDetails?.meta?.secondPaymentAmountForNonTech} payment.</p>
                    </div>
                    <div className="flex flex-col gap-6 py-8 max-w-[584px]">
                        {pointersData.map((pointer, index) => (
                            <div key={index} className="flex items-start gap-4">
                                <img className="min-w-[36px] min-h-[36px] w-[36px] h-[36px]" src={pointer.icon} alt={pointer.title} />
                                <div>
                                    <p className="flex text-[#28293D] text-[18px] leading-[24px] font-inter font-[400] items-center justify-center mt-2">{pointer.title}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col md:w-[589px] py-[13.028px_21.713px] justify-center md:items-center gap-[10.857px] flex-shrink-0 mx-auto">
                    <Button
                        size="lg"
                        color="purplePlaced"
                        className="text-[#FFF] font-inter text-[17px] md:text-[20px] font-semibold leading-[26.056px] w-full mt-2 md:mt-0"
                        onClick={() => handleCreatePayment()}
                    // disabled={days > 0 || hours > 0 || minutes > 0 || seconds > 0}
                    >
                        SECURE YOUR FUTURE FOR ₹{driveRegistrationFormData?.jobType == 'Tech_Jobs' ? driveRegistrationApplication?.driveDetails?.meta?.secondPaymentAmount : driveRegistrationApplication?.driveDetails?.meta?.secondPaymentAmountForNonTech}
                    </Button>
                    {(days > 0 || hours > 0 || minutes > 0 || seconds > 0) && (
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-1 md:gap-[10px]">
                            <p className='text-[#3470E4] font-[inter] text-[14px] font-semibold leading-[24px] whitespace-nowrap'>
                                Valid till
                            </p>
                            <div
                                className={`flex flex-row items-center gap-1 p-[12px] rounded-[8px] bg-[var(--Extended-Blue-100,#D6E2FA)] w-[290px] ${days > 0 ? "md:max-w-[380px] w-full" : "md:max-w-[305px] w-full"}`}
                            >
                                <img src={"https://masai-website-images.s3.ap-south-1.amazonaws.com/proicons_timer_ezgif_com_png_to_webp_converter_d474ff532b.webp"} alt="" />
                                <div className="text-[#7777E7] font-Inter text-[12.973px] font-semibold leading-[20.18px] flex flex-row gap-1 md:gap-2 flex-wrap">
                                    {days > 0 ? (
                                        <>
                                            <p>{days} Days</p>
                                            <p>:</p>
                                        </>
                                    ) : null}
                                    <p>{hours.toString().padStart(2, "0")} hrs</p>
                                    <p>:</p>
                                    <p>{minutes.toString().padStart(2, "0")} min</p>
                                    <p>:</p>
                                    <p>{seconds.toString().padStart(2, "0")} sec</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    )
}
export default PlacementPreparationPage;